<template>
  <div class="travel-expense-management">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>报销申请</span>
          <el-button type="primary" @click="openEditDialog()">新增报销</el-button>
        </div>
      </template>

      <!-- 搜索表单 -->
      <el-form :model="queryParams" class="search-form" label-width="80px">
        <el-row :gutter="16">
          <!-- 状态 -->
          <el-col :span="4">
            <el-form-item label="状态">
              <el-select
                v-model="queryParams.status"
                placeholder="全部"
                clearable
                style="width: 100%"
              >
                <el-option label="全部" value="" />
                <el-option label="草稿" value="draft" />
                <el-option label="待审批" value="pending" />
                <el-option label="审批中" value="approving" />
                <el-option label="已通过" value="approved" />
                <el-option label="已驳回" value="rejected" />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- 报销类型 -->
          <el-col :span="4">
            <el-form-item label="报销类型">
              <el-select
                v-model="queryParams.expenseType"
                placeholder="全部"
                clearable
                style="width: 100%"
              >
                <el-option label="全部" value="" />
                <el-option label="差旅报销" value="TRAVEL" />
                <el-option label="事项报销" value="ITEM" />
              </el-select>
            </el-form-item>
          </el-col>

          <!-- 报销事由 -->
          <el-col :span="5">
            <el-form-item label="报销事由">
              <el-input
                v-model="queryParams.expenseReason"
                placeholder="请输入报销事由"
                clearable
                style="width: 100%"
                @keyup.enter="handleSearch"
              />
            </el-form-item>
          </el-col>

          <!-- 创建时间 -->
          <el-col :span="7">
            <el-form-item label="创建时间">
              <el-date-picker
                v-model="createTimeRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                format="YYYY-MM-DD"
                value-format="YYYY-MM-DD"
                style="width: 100%"
              />
            </el-form-item>
          </el-col>

          <!-- 操作按钮 -->
          <el-col :span="4">
            <el-form-item label=" ">
              <div class="action-buttons-inline">
                <el-button type="primary" @click="handleSearch">查询</el-button>
                <el-button @click="handleReset">重置</el-button>
              </div>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <!-- 报销列表（字段参考小程序新增报销申请） -->
      <div class="table-wrapper">
        <el-table v-loading="loading" :data="expenseList" border stripe style="width: 100%" table-layout="auto">
        <!-- 报销人 -->
        <el-table-column prop="employeeName" label="报销人" width="140" show-overflow-tooltip />

        <!-- 报销金额（元） -->
        <el-table-column prop="expenseAmount" label="报销金额 (元)" width="150" align="right">
          <template #default="{ row }">
            <span style="color: #f56c6c; font-weight: bold">
              ¥{{ formatMoney(row.expenseAmount || row.totalAmount || 0) }}
            </span>
          </template>
        </el-table-column>

        <!-- 状态 -->
        <el-table-column prop="status" label="状态" width="120" align="center">
          <template #default="{ row }">
            <el-tag :type="getStatusType(row.status)" size="small">
              {{ getStatusName(row.status) }}
            </el-tag>
          </template>
        </el-table-column>

        <!-- 出行日期/申报日期 -->
        <el-table-column prop="travelDate" :label="getTravelDateColumnLabel()" width="140">
          <template #default="{ row }">
            {{ row.travelDate || '-' }}
          </template>
        </el-table-column>

        <!-- 结束日期（仅差旅报销显示） -->
        <el-table-column prop="endDate" label="结束日期" width="140">
          <template #default="{ row }">
            {{ row.expenseType === 'TRAVEL' ? (row.endDate || '-') : '-' }}
          </template>
        </el-table-column>

        <!-- 报销类型 -->
        <el-table-column prop="expenseType" label="报销类型" width="120">
          <template #default="{ row }">
            {{ getExpenseTypeName(row.expenseType) }}
          </template>
        </el-table-column>

        <!-- 报销事由 -->
        <el-table-column prop="expenseReason" label="报销事由" min-width="220" show-overflow-tooltip>
          <template #default="{ row }">
            {{ getReasonSummary(row.expenseReason) }}
          </template>
        </el-table-column>

        <!-- 创建时间 -->
        <el-table-column prop="createTime" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDateTime(row.createTime) }}
          </template>
        </el-table-column>

        <el-table-column label="操作" width="320" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleView(row)">详情</el-button>
            <el-button
              type="warning"
              size="small"
              @click="openEditDialog(row)"
              v-if="row.status === 'draft'"
            >
              编辑
            </el-button>
            <el-tooltip content="修正并重新发起审批" placement="top">
              <el-button
                type="success"
                size="small"
                @click="handleCorrect(row)"
                v-if="row.status === 'rejected' || row.status === 'REJECTED'"
              >
                修正
              </el-button>
            </el-tooltip>
            <el-button
              type="danger"
              size="small"
              @click="handleWithdraw(row)"
              v-if="row.status === 'pending'"
            >
              撤销
            </el-button>
            <el-button type="info" size="small" @click="handleViewApprovalProgress(row)" v-if="row.status !== 'draft'">审批进度</el-button>
          </template>
        </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="queryParams.page"
          v-model:page-size="queryParams.pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <!-- 报销申请详情弹窗 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="报销申请详情"
      width="700px"
      destroy-on-close
    >
      <el-skeleton v-if="detailLoading" :rows="6" animated />
      <div v-else-if="detailInfo" class="expense-detail">
        <div class="expense-detail-layout">
          <!-- 左侧：基础信息 -->
          <div class="expense-detail-left">
            <el-descriptions :column="1" border>
              <el-descriptions-item label="报销人">
                {{ detailInfo.employeeName || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="报销金额">
                <span class="amount-text">¥{{ formatMoney(detailInfo.expenseAmount || 0) }}</span>
              </el-descriptions-item>
              <el-descriptions-item label="状态">
                <el-tag :type="getStatusType(detailInfo.status)" size="small">
                  {{ getStatusName(detailInfo.status) }}
                </el-tag>
              </el-descriptions-item>
              <el-descriptions-item :label="detailInfo.expenseType === 'ITEM' ? '申报日期' : '出行日期'">
                {{ detailInfo.travelDate || '-' }}
              </el-descriptions-item>
              <el-descriptions-item v-if="detailInfo.expenseType === 'TRAVEL'" label="结束日期">
                {{ detailInfo.endDate || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="报销类型">
                {{ getExpenseTypeName(detailInfo.expenseType) }}
              </el-descriptions-item>
              <el-descriptions-item label="提交时间">
                {{ detailInfo.submitTimeFormatted || '-' }}
              </el-descriptions-item>
              <el-descriptions-item 
                v-if="detailInfo.status && detailInfo.status !== 'draft'" 
                label="审批时间"
              >
                {{ detailInfo.approvalTimeFormatted || '-' }}
              </el-descriptions-item>
              <el-descriptions-item label="报销事由">
                <div class="detail-content">
                  {{ detailInfo.expenseReason || '-' }}
                </div>
              </el-descriptions-item>
              <el-descriptions-item label="附件">
                <div v-if="detailInfo.attachments && detailInfo.attachments.length" class="attachment-list">
                  <!-- 图片附件：显示缩略图 -->
                  <div
                    v-for="(attachment, index) in detailInfo.attachments"
                    :key="index"
                    :class="['attachment-item', { 'attachment-item-image': isAttachmentImage(attachment) }]"
                  >
                    <template v-if="isAttachmentImage(attachment)">
                      <div class="attachment-image-wrapper">
                        <el-image
                          :src="getAttachmentUrl(attachment)"
                          :alt="getAttachmentFileName(attachment)"
                          :preview-src-list="getImagePreviewList()"
                          :initial-index="getImageIndex(index)"
                          fit="cover"
                          class="attachment-image-preview"
                          :preview-teleported="true"
                          crossorigin="anonymous"
                          referrerpolicy="no-referrer-when-downgrade"
                          @error="handleImageError"
                        >
                          <template #error>
                            <div class="image-slot">
                              <el-icon><Picture /></el-icon>
                            </div>
                          </template>
                        </el-image>
                        <!-- 下载按钮覆盖层 -->
                        <div class="download-overlay">
                          <el-button
                            type="primary"
                            :icon="Download"
                            circle
                            size="small"
                            class="download-overlay-btn"
                            @click.stop="handleDownloadAttachment(attachment)"
                            title="下载图片"
                          />
                        </div>
                      </div>
                      <div class="attachment-image-info">
                        <div class="attachment-image-details">
                          <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                            {{ getAttachmentFileName(attachment) }}
                          </span>
                          <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                            ({{ formatFileSize(getAttachmentSize(attachment)) }})
                          </span>
                        </div>
                      </div>
                    </template>
                    
                    <!-- 非图片附件：显示图标 -->
                    <template v-else>
                      <div :class="['attachment-icon-wrapper', getFileIconClass(attachment)]">
                        <el-icon class="attachment-icon">
                          <component :is="getFileIcon(attachment)" />
                        </el-icon>
                        <span v-if="getFileTypeLabel(attachment)" class="file-type-label">
                          {{ getFileTypeLabel(attachment) }}
                        </span>
                      </div>
                      <span class="attachment-name" :title="getAttachmentFileName(attachment)">
                        {{ getAttachmentFileName(attachment) }}
                      </span>
                      <span v-if="getAttachmentSize(attachment) > 0" class="attachment-size">
                        ({{ formatFileSize(getAttachmentSize(attachment)) }})
                      </span>
                      <div class="attachment-actions">
                        <el-button
                          type="primary"
                          size="small"
                          :icon="Download"
                          link
                          @click="handleDownloadAttachment(attachment)"
                        >
                          下载
                        </el-button>
                      </div>
                    </template>
                  </div>
                </div>
                <span v-else>无附件</span>
              </el-descriptions-item>
            </el-descriptions>
          </div>

          <!-- 右侧：审批记录 -->
          <div class="expense-detail-right" v-if="detailInfo.status && detailInfo.status !== 'draft'">
            <el-card class="approval-record-card" shadow="never">
              <template #header>
                <div class="card-title">
                  <span class="title-text">审批记录</span>
                </div>
              </template>
              <div v-loading="approvalRecordLoading">
                <ApprovalTimeline :records="approvalRecords" />
              </div>
            </el-card>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <!-- 待审批状态：显示撤销按钮 -->
          <div
            v-if="detailInfo && detailInfo.status === 'pending'"
            class="footer-center"
          >
            <el-button type="danger" size="large" style="width: 200px" @click="handleWithdrawInDetail">
              撤销
            </el-button>
          </div>
          <!-- 草稿状态：显示编辑和提交审批按钮 -->
          <div v-else-if="detailInfo && detailInfo.status === 'draft'" class="footer-buttons">
            <el-button @click="openEditDialog(detailInfo)">编辑</el-button>
            <div style="position: relative; display: inline-block;">
              <div v-if="isPersonalUser" style="position: absolute; top: -20px; right: 0; font-size: 12px; color: #67c23a; white-space: nowrap;">
                个人用户点提交审批直接通过
              </div>
              <el-button class="submit-approval-btn" @click="handleSubmitFromDetail">提交审批</el-button>
            </div>
          </div>
          <!-- 已驳回状态：显示修正按钮 -->
          <div
            v-else-if="detailInfo && (detailInfo.status === 'rejected' || detailInfo.status === 'REJECTED')"
            class="footer-center"
          >
            <el-tooltip content="修正并重新发起审批" placement="top">
              <el-button type="success" size="large" style="width: 200px" @click="handleCorrectFromDetail">
                修正
              </el-button>
            </el-tooltip>
          </div>
          <!-- 其他状态：只显示关闭按钮 -->
          <div v-else class="footer-right">
            <el-button @click="detailDialogVisible = false">关闭</el-button>
          </div>
        </div>
      </template>
    </el-dialog>

    <!-- 新增/编辑报销申请弹窗 -->
    <el-dialog
      v-model="editDialogVisible"
      :title="isCorrectMode ? '修正报销申请' : (isEdit ? '编辑报销申请' : '新增报销申请')"
      width="700px"
      destroy-on-close
    >
      <el-form
        ref="expenseFormRef"
        :model="expenseForm"
        :rules="expenseFormRules"
        label-width="110px"
        class="expense-form"
      >
        <el-form-item label="报销金额 (元)" prop="expenseAmount">
          <el-input
            v-model="expenseForm.expenseAmount"
            type="number"
            placeholder="请输入报销金额"
          >
            <template #prefix>¥</template>
          </el-input>
        </el-form-item>

        <el-form-item label="报销类型" prop="expenseType">
          <el-select v-model="expenseForm.expenseType" placeholder="请选择报销类型" style="width: 100%">
            <el-option label="差旅报销" value="TRAVEL" />
            <el-option label="事项报销" value="ITEM" />
          </el-select>
        </el-form-item>

        <el-form-item :label="travelDateLabel" prop="travelDate" class="no-wrap-label">
          <el-date-picker
            v-model="expenseForm.travelDate"
            :placeholder="travelDatePlaceholder"
            type="date"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item v-if="expenseForm.expenseType === 'TRAVEL'" :label="endDateLabel" prop="endDate" class="no-wrap-label">
          <el-date-picker
            v-model="expenseForm.endDate"
            type="date"
            placeholder="请选择结束日期"
            format="YYYY-MM-DD"
            value-format="YYYY-MM-DD"
            style="width: 100%"
          />
        </el-form-item>

        <el-form-item label="报销事由" prop="expenseReason">
          <el-input
            v-model="expenseForm.expenseReason"
            type="textarea"
            :rows="4"
            maxlength="500"
            show-word-limit
            placeholder="请输入本次差旅的事由、行程等信息"
          />
        </el-form-item>

        <el-form-item label="附件">
          <AttachmentUpload 
            ref="expenseAttachmentUploadRef"
            v-model="expenseForm.attachments"
            :max-count="10"
            :business-type="'TRAVEL_EXPENSE'"
            :module="'报销申请'"
            :show-download="false"
          />
        </el-form-item>
      </el-form>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="editDialogVisible = false">取消</el-button>
          <el-button type="info" :loading="submitLoading" @click="saveDraft" style="margin-right: 12px;">
            保存草稿
          </el-button>
          <div style="position: relative; display: inline-block;">
            <div v-if="isPersonalUser" style="position: absolute; top: -20px; right: 0; font-size: 12px; color: #67c23a; white-space: nowrap;">
              个人用户点提交审批直接通过
            </div>
            <el-button class="submit-approval-btn" :loading="submitLoading" @click="submitExpense">
              提交审批
            </el-button>
          </div>
        </div>
      </template>
    </el-dialog>

    <!-- 审批进度对话框 -->
    <el-dialog
      v-model="approvalProgressDialogVisible"
      title="审批进度"
      width="600px"
      @close="approvalProgressDialogVisible = false"
    >
      <div v-loading="approvalProgressLoading" class="approval-progress-dialog">
        <div class="approval-timeline-wrapper">
          <ApprovalTimeline :records="approvalProgressRecords" />
        </div>
      </div>
      <template #footer>
        <el-button @click="approvalProgressDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import ApprovalTimeline from '../components/ApprovalTimeline.vue'
import {
  getTravelExpenseList,
  getTravelExpenseDetail,
  createTravelExpense,
  updateTravelExpense,
  submitTravelExpense,
  withdrawTravelExpense,
  getApprovalRecords
} from '../api/salesman'
import { recordUpload, formatFileSize, isImageFile, downloadFileFromServer } from '../utils/fileUtils'
import AttachmentUpload from '../components/AttachmentUpload.vue'
import { getFileName, getPreviewUrl } from '../api/file'
import { Download, Picture, Document, VideoPlay } from '@element-plus/icons-vue'

// 获取企业ID（用于判断是否是个人用户）
const getCompanyId = () => {
  try {
    const userInfoStr = localStorage.getItem('userInfo')
    if (userInfoStr) {
      const userInfo = JSON.parse(userInfoStr)
      return userInfo.companyId || null
    }
  } catch (e) {
    console.warn('解析用户信息失败', e)
  }
  return null
}

// 判断是否是个人用户（没有企业ID）
const isPersonalUser = computed(() => {
  return !getCompanyId()
})

const loading = ref(false)
const expenseList = ref([])
const total = ref(0)
const queryParams = reactive({
  status: '',
  expenseType: '',
  expenseReason: '',
  startDate: '',
  endDate: '',
  page: 1,
  pageSize: 10
})

// 创建时间范围（用于日期选择器）
const createTimeRange = ref([])

// 详情相关
const detailDialogVisible = ref(false)
const detailLoading = ref(false)
const detailInfo = ref(null)
const approvalRecords = ref([])
const approvalRecordLoading = ref(false)

// 审批进度对话框
const approvalProgressDialogVisible = ref(false)
const approvalProgressLoading = ref(false)
const approvalProgressRecords = ref([])

// 编辑相关
const editDialogVisible = ref(false)
const expenseAttachmentUploadRef = ref(null)
const isEdit = ref(false)
const isCorrectMode = ref(false) // 修正模式标志
const correctId = ref(null) // 修正时的原ID
const submitLoading = ref(false)
const expenseFormRef = ref(null)

const expenseForm = reactive({
  id: null,
  expenseAmount: null,
  expenseReason: '',
  expenseType: 'TRAVEL',
  travelDate: '',
  endDate: '',
  attachments: [] // 附件列表，格式：[{url: string, fileName: string, size: number}]
})

const expenseFormRules = {
  expenseAmount: [
    { required: true, message: '请输入报销金额', trigger: 'blur' },
    {
      validator: (_rule, value, callback) => {
        const num = Number(value)
        if (!value || isNaN(num) || num <= 0) {
          callback(new Error('报销金额必须大于0'))
        } else {
          callback()
        }
      },
      trigger: 'blur'
    }
  ],
  expenseType: [{ required: true, message: '请选择报销类型', trigger: 'change' }],
  travelDate: [{ required: true, message: '请选择出行日期', trigger: 'change' }],
  endDate: [
    {
      validator: (_rule, value, callback) => {
        const type = expenseForm.expenseType
        const start = expenseForm.travelDate
        if (type === 'TRAVEL') {
          if (!value) return callback(new Error('请选择结束日期'))
          if (start && value < start) return callback(new Error('结束日期不能早于出行日期'))
        } else if (value && start && value < start) {
          return callback(new Error('结束日期不能早于出行日期'))
        }
        callback()
      },
      trigger: 'change'
    }
  ],
  expenseReason: [{ required: true, message: '请输入报销事由', trigger: 'blur' }]
}

// 提交状态
const submitting = ref(false)

const getStatusName = (status) => {
  const key = (status || '').toString().toLowerCase().trim()
  const map = {
    draft: '草稿',
    pending: '待审批',
    approving: '审批中',
    approved: '已通过',
    rejected: '已驳回',
    cancelled: '已撤销'
  }
  return map[key] || '-'
}

const getStatusType = (status) => {
  const key = (status || '').toString().toLowerCase().trim()
  const map = {
    draft: 'info',
    pending: 'warning',
    approving: 'warning',
    approved: 'success',
    rejected: 'danger',
    cancelled: 'info'
  }
  return map[key] || 'info'
}

const formatMoney = (amount) => {
  if (!amount) return '0.00'
  return Number(amount).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const getExpenseTypeName = (type) => {
  const key = (type || '').toUpperCase()
  if (key === 'ITEM') return '事项报销'
  return '差旅报销'
}

// 获取列表表头标签（根据筛选条件动态显示）
const getTravelDateColumnLabel = () => {
  // 如果筛选条件中指定了报销类型，根据类型显示
  if (queryParams.expenseType === 'ITEM') {
    return '申报日期'
  }
  // 如果筛选条件中指定了差旅报销，显示出行日期
  if (queryParams.expenseType === 'TRAVEL') {
    return '出行日期'
  }
  // 默认显示"出行日期/申报日期"（因为列表可能同时包含两种类型）
  return '出行日期/申报日期'
}

const travelDateLabel = computed(() => (expenseForm.expenseType === 'ITEM' ? '申报日期' : '出行日期'))
const travelDatePlaceholder = computed(() => (expenseForm.expenseType === 'ITEM' ? '请选择申报日期' : '请选择出行日期'))
const endDateLabel = computed(() => (expenseForm.expenseType === 'TRAVEL' ? '* 结束日期' : '结束日期'))

watch(
  () => expenseForm.travelDate,
  (val) => {
    if (expenseForm.endDate && val && expenseForm.endDate < val) {
      expenseForm.endDate = ''
      ElMessage.warning('结束日期不能早于出行日期')
    }
  }
)

watch(
  () => expenseForm.expenseType,
  (type) => {
    // 切换为事项报销时，结束日期可选；切回报销申请如已选并早于出行日期则清空
    if (type === 'TRAVEL' && expenseForm.endDate && expenseForm.travelDate && expenseForm.endDate < expenseForm.travelDate) {
      expenseForm.endDate = ''
      ElMessage.warning('结束日期不能早于出行日期')
    }
  }
)

const formatDateTime = (dateTime) => {
  if (!dateTime) return '-'
  return new Date(dateTime).toLocaleString('zh-CN')
}

// 列表中报销事由摘要
const getReasonSummary = (reason) => {
  if (!reason) return '-'
  const str = String(reason).trim()
  return str.length > 30 ? `${str.slice(0, 30)}...` : str
}


// 审批节点状态中文
const getApprovalStatusName = (status) => {
  const upper = (status || '').toString().toUpperCase()
  const map = {
    PENDING: '待审批',
    APPROVING: '审批中',
    APPROVED: '已通过',
    REJECTED: '已驳回',
    WAITING: '等待中',
    CANCELLED: '已取消'
  }
  return map[upper] || upper || '-'
}



const processApprovalRecords = (records) => {
  if (!Array.isArray(records) || records.length === 0) {
    return []
  }

  // 不再过滤 CANCELLED/CANCELED，确保驳回后续节点也可展示审批人
  const sortedRecords = [...records].sort((a, b) => {
    if (a.nodeOrder !== undefined && b.nodeOrder !== undefined) {
      return a.nodeOrder - b.nodeOrder
    }
    if (a.currentNodeIndex !== undefined && b.currentNodeIndex !== undefined) {
      return a.currentNodeIndex - b.currentNodeIndex
    }
    const timeA = a.createTime ? new Date(a.createTime).getTime() : 0
    const timeB = b.createTime ? new Date(b.createTime).getTime() : 0
    return timeA - timeB
  })

  const rejectedIndex = sortedRecords.findIndex(r => {
    const status = String(r.approvalStatus || '').toUpperCase().trim()
    return status === 'REJECTED'
  })
  const hasRejected = rejectedIndex !== -1

  return sortedRecords.map((record, index) => {
    let finalStatus = record.approvalStatus
    if (hasRejected && index > rejectedIndex) {
      finalStatus = 'CANCELLED'
    } else if (!finalStatus || String(finalStatus).trim() === '') {
      finalStatus = 'PENDING'
    }
    const normalizedStatus = finalStatus ? String(finalStatus).toUpperCase().trim() : 'PENDING'

    return {
      ...record,
      currentNode: record.currentNode || record.nodeName || '审批节点',
      approverName: record.approver || record.approverName || '-',
      statusName: getApprovalStatusName(normalizedStatus),
      createTimeFormatted: record.createTime ? formatDateTime(record.createTime) : '',
      approvalStatus: normalizedStatus
    }
  })
}

const loadExpenseList = async () => {
  loading.value = true
  try {
    // 构建请求参数，确保所有筛选条件都被正确传递
    const params = {
      page: queryParams.page || 1,
      pageSize: queryParams.pageSize || 10
    }
    
    // 只传递有值的筛选条件
    if (queryParams.status) {
      params.status = queryParams.status
    }
    if (queryParams.expenseType) {
      params.expenseType = queryParams.expenseType
    }
    if (queryParams.expenseReason) {
      params.expenseReason = queryParams.expenseReason
    }
    if (queryParams.startDate) {
      params.startDate = queryParams.startDate
    }
    if (queryParams.endDate) {
      params.endDate = queryParams.endDate
    }
    
    console.log('报销申请列表请求参数:', params)
    const res = await getTravelExpenseList(params)
    if (res.code === '000000') {
      console.log('报销申请列表接口返回：', res.data)
      const data = res.data || {}
      // 兼容多种分页数据结构
      const list =
        data.records ||
        data.list ||
        data.rows ||
        data.data ||
        (Array.isArray(data) ? data : [])

      expenseList.value = Array.isArray(list) ? list : []
      total.value = data.total || data.count || data.totalCount || expenseList.value.length || 0
    }
  } catch (error) {
    console.error('获取报销申请列表失败:', error)
    ElMessage.error('获取报销申请列表失败')
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  // 处理报销事由
  if (queryParams.expenseReason) {
    queryParams.expenseReason = queryParams.expenseReason.trim()
  }

  // 处理报销类型：如果为空字符串，确保传递空字符串（后端会忽略）
  if (!queryParams.expenseType) {
    queryParams.expenseType = ''
  }

  // 处理创建时间范围
  if (createTimeRange.value && createTimeRange.value.length === 2) {
    queryParams.startDate = createTimeRange.value[0]
    queryParams.endDate = createTimeRange.value[1]
  } else {
    queryParams.startDate = ''
    queryParams.endDate = ''
  }
  
  queryParams.page = 1
  loadExpenseList()
}

const handleReset = () => {
  queryParams.status = ''
  queryParams.expenseType = ''
  queryParams.expenseReason = ''
  queryParams.startDate = ''
  queryParams.endDate = ''
  queryParams.page = 1
  createTimeRange.value = []
  loadExpenseList()
}

// 查看详情
const handleView = async (row) => {
  detailDialogVisible.value = true
  detailLoading.value = true
  detailInfo.value = null
  approvalRecords.value = []

  try {
    const res = await getTravelExpenseDetail(row.id)
    if (res.code !== '000000' || !res.data) {
      throw new Error(res.msg || '获取报销申请详情失败')
    }
    const data = res.data

    const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
    
    // 处理附件数据（优先使用后端返回的附件信息，包含文件大小）
    // 注意：图片类型的附件会通过 AttachmentUpload 组件显示，支持预览和下载
    let attachments = []
    // 优先使用 attachmentInfos（包含文件大小信息）
    if (data.attachmentInfos && Array.isArray(data.attachmentInfos) && data.attachmentInfos.length > 0) {
      attachments = data.attachmentInfos.map(att => {
        let url = att.fileUrl || att.url || att
        if (typeof url === 'string' && !url.startsWith('http://') && !url.startsWith('https://')) {
          url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
        }
        return {
          url,
          fileName: att.fileName || url.split('/').pop() || '附件',
          originalFileName: att.originalFileName || att.fileName || url.split('/').pop() || '附件',
          size: att.size || 0
        }
      })
    } else if (data.attachments) {
      if (Array.isArray(data.attachments)) {
        attachments = await Promise.all(data.attachments.map(async (att) => {
          if (typeof att === 'string') {
            let url = att
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
              url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
            }
            // 尝试从后端获取原始文件名和文件大小
            let originalFileName = url.split('/').pop() || '附件'
            let fileSize = 0
            try {
              const fileNameRes = await getFileName(url)
              if (fileNameRes && fileNameRes.code === '000000') {
                originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
              }
            } catch (error) {
              console.debug('获取文件名失败:', error)
            }
            // 尝试通过 HEAD 请求获取文件大小
            try {
              const response = await fetch(url, { method: 'HEAD' })
              if (response.ok) {
                const contentLength = response.headers.get('Content-Length')
                if (contentLength) {
                  fileSize = parseInt(contentLength, 10) || 0
                }
              }
            } catch (error) {
              console.debug('获取文件大小失败:', error)
            }
            return { url, fileName: url.split('/').pop() || '附件', originalFileName, size: fileSize }
          }
          return att
        }))
      } else if (typeof data.attachments === 'string') {
        try {
          const parsed = JSON.parse(data.attachments)
          const urls = Array.isArray(parsed) ? parsed : [parsed]
          attachments = await Promise.all(urls.map(async (url) => {
            if (typeof url === 'string') {
              let fullUrl = url
              if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
                fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
              }
              // 尝试从后端获取原始文件名和文件大小
              let originalFileName = fullUrl.split('/').pop() || '附件'
              let fileSize = 0
              try {
                const fileNameRes = await getFileName(fullUrl)
                if (fileNameRes && fileNameRes.code === '000000') {
                  originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
                }
              } catch (error) {
                console.debug('获取文件名失败:', error)
              }
              // 尝试通过 HEAD 请求获取文件大小
              try {
                const response = await fetch(fullUrl, { method: 'HEAD' })
                if (response.ok) {
                  const contentLength = response.headers.get('Content-Length')
                  if (contentLength) {
                    fileSize = parseInt(contentLength, 10) || 0
                  }
                }
              } catch (error) {
                console.debug('获取文件大小失败:', error)
              }
              return { url: fullUrl, fileName: fullUrl.split('/').pop() || '附件', originalFileName, size: fileSize }
            }
            return url
          }))
        } catch {
          const urlArray = data.attachments.split(',').map(s => s.trim()).filter(Boolean)
          attachments = await Promise.all(urlArray.map(async (url) => {
            let fullUrl = url
            if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
              fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
            }
            // 尝试从后端获取原始文件名
            let originalFileName = fullUrl.split('/').pop() || '附件'
            try {
              const fileNameRes = await getFileName(fullUrl)
              if (fileNameRes && fileNameRes.code === '000000') {
                originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
              }
            } catch (error) {
              console.debug('获取文件名失败:', error)
            }
            return { url: fullUrl, fileName: fullUrl.split('/').pop() || '附件', originalFileName, size: 0 }
          }))
        }
      }
    }

    detailInfo.value = {
      id: data.id,
      expenseNumber: data.expenseNumber || row.expenseNumber || '',
      employeeName: data.employeeName || '',
      expenseAmount: data.expenseAmount,
      expenseReason: data.expenseReason,
      status: data.status || row.status,
      travelDate: data.travelDate || '',
      endDate: data.endDate || '',
      expenseType: data.expenseType || 'TRAVEL',
      submitTimeFormatted: data.submitTime || '',
      approvalTimeFormatted: data.approvalTime || '',
      attachments
    }

    // 加载审批记录（草稿状态不加载）
    if (detailInfo.value.status && detailInfo.value.status !== 'draft') {
      approvalRecordLoading.value = true
      try {
        const approvalRes = await getApprovalRecords('TRAVEL_EXPENSE', detailInfo.value.id)
        if (approvalRes.code === '000000') {
          approvalRecords.value = processApprovalRecords(approvalRes.data || [])
        } else {
          ElMessage.error(approvalRes.message || '获取审批记录失败')
        }
      } catch (error) {
        console.error('获取审批记录失败:', error)
        ElMessage.error('获取审批记录失败')
      } finally {
        approvalRecordLoading.value = false
      }
    }
  } catch (error) {
    console.error('获取报销申请详情失败:', error)
    detailDialogVisible.value = false
    ElMessage.error(error.message || '获取报销申请详情失败')
  } finally {
    detailLoading.value = false
  }
}

// 打开新增/编辑弹窗
const openEditDialog = async (row, correct = false) => {
  if (row && row.id) {
    // 编辑模式或修正模式
    isEdit.value = !correct // 修正模式下不是编辑模式
    isCorrectMode.value = correct
    correctId.value = correct ? row.id : null
    
    try {
      loading.value = true
      const res = await getTravelExpenseDetail(row.id)
      if (res.code !== '000000' || !res.data) {
        throw new Error(res.msg || '获取报销申请详情失败')
      }
      const data = res.data

      const baseURL = process.env.VUE_APP_BASE_API || 'http://localhost:8086'
      
      // 处理附件数据（优先使用后端返回的附件信息，包含文件大小）
      // 注意：图片类型的附件会通过 AttachmentUpload 组件显示，支持预览和下载
      let attachments = []
      // 优先使用 attachmentInfos（包含文件大小信息）
      if (data.attachmentInfos && Array.isArray(data.attachmentInfos) && data.attachmentInfos.length > 0) {
        attachments = data.attachmentInfos.map(att => {
          let url = att.fileUrl || att.url || att
          if (typeof url === 'string' && !url.startsWith('http://') && !url.startsWith('https://')) {
            url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
          }
          return {
            url,
            fileName: att.fileName || url.split('/').pop() || '附件',
            originalFileName: att.originalFileName || att.fileName || url.split('/').pop() || '附件',
            size: att.size || 0
          }
        })
      } else if (data.attachments) {
        if (Array.isArray(data.attachments)) {
          attachments = await Promise.all(data.attachments.map(async (att) => {
            if (typeof att === 'string') {
              let url = att
              if (!url.startsWith('http://') && !url.startsWith('https://')) {
                url = url.startsWith('/') ? baseURL + url : `${baseURL}/${url}`
              }
              // 尝试从后端获取原始文件名和文件大小
              let originalFileName = url.split('/').pop() || '附件'
              let fileSize = 0
              try {
                const fileNameRes = await getFileName(url)
                if (fileNameRes && fileNameRes.code === '000000') {
                  originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
                }
              } catch (error) {
                console.debug('获取文件名失败:', error)
              }
              // 尝试通过 HEAD 请求获取文件大小
              try {
                const response = await fetch(url, { method: 'HEAD' })
                if (response.ok) {
                  const contentLength = response.headers.get('Content-Length')
                  if (contentLength) {
                    fileSize = parseInt(contentLength, 10) || 0
                  }
                }
              } catch (error) {
                console.debug('获取文件大小失败:', error)
              }
              return { 
                url, 
                fileName: originalFileName, // 使用原始文件名
                originalFileName, // 保存原始文件名
                size: fileSize 
              }
            }
            return att
          }))
      } else if (typeof data.attachments === 'string') {
        try {
          const parsed = JSON.parse(data.attachments)
          const urls = Array.isArray(parsed) ? parsed : [parsed]
          attachments = await Promise.all(urls.map(async (url) => {
            if (typeof url === 'string') {
              let fullUrl = url
              if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
                fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
              }
              // 尝试从后端获取原始文件名和文件大小
              let originalFileName = fullUrl.split('/').pop() || '附件'
              let fileSize = 0
              try {
                const fileNameRes = await getFileName(fullUrl)
                if (fileNameRes && fileNameRes.code === '000000') {
                  originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
                }
              } catch (error) {
                console.debug('获取文件名失败:', error)
              }
              // 尝试通过 HEAD 请求获取文件大小
              try {
                const response = await fetch(fullUrl, { method: 'HEAD' })
                if (response.ok) {
                  const contentLength = response.headers.get('Content-Length')
                  if (contentLength) {
                    fileSize = parseInt(contentLength, 10) || 0
                  }
                }
              } catch (error) {
                console.debug('获取文件大小失败:', error)
              }
              return { url: fullUrl, fileName: fullUrl.split('/').pop() || '附件', originalFileName, size: fileSize }
            }
            return url
          }))
        } catch {
          const urlArray = data.attachments.split(',').map(s => s.trim()).filter(Boolean)
          attachments = await Promise.all(urlArray.map(async (url) => {
            let fullUrl = url
            if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
              fullUrl = fullUrl.startsWith('/') ? baseURL + fullUrl : `${baseURL}/${fullUrl}`
            }
            // 尝试从后端获取原始文件名
            let originalFileName = fullUrl.split('/').pop() || '附件'
            try {
              const fileNameRes = await getFileName(fullUrl)
              if (fileNameRes && fileNameRes.code === '000000') {
                originalFileName = fileNameRes.data || fileNameRes.msg || originalFileName
              }
            } catch (error) {
              console.debug('获取文件名失败:', error)
            }
            return { url: fullUrl, fileName: fullUrl.split('/').pop() || '附件', originalFileName, size: 0 }
          }))
        }
      }
    }

    Object.assign(expenseForm, {
        id: correct ? null : data.id, // 修正模式下不设置id，创建新数据
        expenseAmount: data.expenseAmount,
        expenseReason: data.expenseReason,
      expenseType: data.expenseType || 'TRAVEL',
      travelDate: data.travelDate,
      endDate: data.endDate || '',
        attachments
      })
    } catch (error) {
      console.error('编辑报销申请失败:', error)
      ElMessage.error(error.message || '加载报销申请详情失败')
      return
    } finally {
      loading.value = false
    }
  } else {
    // 新增模式
    isEdit.value = false
    isCorrectMode.value = false
    correctId.value = null
    Object.assign(expenseForm, {
      id: null,
      expenseAmount: null,
      expenseReason: '',
    expenseType: 'TRAVEL',
    travelDate: '',
    endDate: '',
      attachments: []
    })
  }

  editDialogVisible.value = true
}

// 修正功能（从列表页）
const handleCorrect = async (row) => {
  if (!row || !row.id) {
    ElMessage.warning('数据异常，无法修正')
    return
  }
  const status = (row.status || '').toLowerCase()
  if (status !== 'rejected') {
    ElMessage.warning('只有已驳回状态的报销申请可以修正')
    return
  }
  await openEditDialog(row, true)
}

// 修正功能（从详情页）
const handleCorrectFromDetail = async () => {
  if (!detailInfo.value || !detailInfo.value.id) {
    ElMessage.warning('数据异常，无法修正')
    return
  }
  const status = (detailInfo.value.status || '').toLowerCase()
  if (status !== 'rejected') {
    ElMessage.warning('只有已驳回状态的报销申请可以修正')
    return
  }
  // 关闭详情弹窗，打开修正弹窗
  detailDialogVisible.value = false
  await openEditDialog(detailInfo.value, true)
}

const handleWithdraw = async (row) => {
  try {
    await ElMessageBox.confirm('确定要撤销该报销申请吗？', '提示', {
      type: 'warning'
    })
    const res = await withdrawTravelExpense(row.id)
    if (res.code === '000000') {
      ElMessage.success('撤销成功')
      // 撤销后切换到草稿列表
      queryParams.status = 'draft'
      queryParams.page = 1
      loadExpenseList()
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('撤销报销申请失败:', error)
      ElMessage.error('撤销报销申请失败')
    }
  }
}

// 详情中撤销
const handleWithdrawInDetail = async () => {
  if (!detailInfo.value || !detailInfo.value.id) return
  try {
    await ElMessageBox.confirm('确定要撤销该报销申请吗？撤销后将变为草稿状态', '提示', {
      type: 'warning'
    })
    const res = await withdrawTravelExpense(detailInfo.value.id)
    if (res.code === '000000') {
      ElMessage.success('撤销成功')
      detailDialogVisible.value = false
      // 撤销后切换到草稿列表
      queryParams.status = 'draft'
      queryParams.page = 1
      loadExpenseList()
    } else {
      throw new Error(res.msg || '撤销失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('撤销报销申请失败:', error)
      ElMessage.error(error.message || '撤销报销申请失败')
    }
  }
}

// 查看审批进度
const handleViewApprovalProgress = async (row) => {
  approvalProgressLoading.value = true
  approvalProgressDialogVisible.value = true
  try {
    const expenseId = row.id
    if (!expenseId) {
      ElMessage.error('报销申请ID不存在')
      approvalProgressDialogVisible.value = false
      return
    }
    // 加载审批记录
    const res = await getApprovalRecords('TRAVEL_EXPENSE', expenseId)
    if (res.code === '000000') {
      approvalProgressRecords.value = processApprovalRecords(res.data || [])
    } else {
      ElMessage.error(res.message || res.msg || '获取审批记录失败')
      approvalProgressRecords.value = []
    }
  } catch (error) {
    console.error('获取审批记录失败:', error)
    ElMessage.error('获取审批记录失败')
    approvalProgressRecords.value = []
  } finally {
    approvalProgressLoading.value = false
  }
}



// 提交 / 保存公用方法
const doSaveOrSubmit = (action) => {
  if (!expenseFormRef.value) return

  expenseFormRef.value.validate(async (valid) => {
    if (!valid) return

    submitLoading.value = true
    submitting.value = true
    try {
      // 使用附件上传组件的统一上传逻辑（带进度条）
      if (expenseAttachmentUploadRef.value) {
        try {
          const uploadedAttachments = await expenseAttachmentUploadRef.value.uploadAll()
          expenseForm.attachments = uploadedAttachments || expenseForm.attachments
          recordUpload() // 记录上传，用于限流
        } catch (error) {
          ElMessage.error(`附件上传失败: ${error.message}`)
          throw error
        }
      }
      
      const payload = {
        id: expenseForm.id,
        expenseAmount: expenseForm.expenseAmount ? Number(expenseForm.expenseAmount) : null,
        expenseReason: expenseForm.expenseReason?.trim(),
        expenseType: expenseForm.expenseType,
        travelDate: expenseForm.travelDate,
        endDate: expenseForm.endDate || null,
        // 附件：始终传数组，空数组表示清空旧附件
        attachments: Array.isArray(expenseForm.attachments)
          ? expenseForm.attachments
              .map(att => {
                if (typeof att === 'string') return att
                return att?.url || ''
              })
              .filter(url => url)
          : [],
        action
      }

      let res
      if (action === 'submit') {
        // 提交审批
        res = await submitTravelExpense(payload)
      } else {
        // 保存草稿
        // 修正模式下不设置id，创建新数据
        if (expenseForm.id && !isCorrectMode.value) {
          res = await updateTravelExpense(payload)
        } else {
          res = await createTravelExpense(payload)
          if (res.code === '000000' && typeof res.data === 'number') {
            expenseForm.id = res.data
          }
        }
      }

      if (res.code === '000000') {
        const isSubmit = action === 'submit'
        ElMessage.success(isSubmit ? '提交成功' : '保存成功')
        editDialogVisible.value = false

        // 根据操作结果切换列表筛选状态并重新查询
        if (isSubmit) {
          // 个人用户提交审批后，自动切换到已通过状态
          queryParams.status = isPersonalUser.value ? 'approved' : 'pending'
        } else {
          queryParams.status = 'draft'
        }
        queryParams.page = 1
        loadExpenseList()

        // 如果详情弹窗正在打开且就是当前单据，刷新详情回显最新值
        if (detailDialogVisible.value && detailInfo.value && detailInfo.value.id === payload.id) {
          await handleView({ id: payload.id })
        }
      } else {
        throw new Error(res.msg || '操作失败')
      }
    } catch (error) {
      console.error('保存/提交报销申请失败:', error)
      ElMessage.error(error.message || '操作失败')
    } finally {
      submitLoading.value = false
      submitting.value = false
    }
  })
}

// 保存草稿
const saveDraft = () => {
  doSaveOrSubmit('draft')
}

// 提交审批
const submitExpense = () => {
  doSaveOrSubmit('submit')
}

// 判断附件是否为图片
const isAttachmentImage = (attachment) => {
  if (!attachment) return false
  const fileName = getAttachmentFileName(attachment)
  return isImageFile(fileName)
}

// 获取附件文件名（优先显示原始文件名）
const getAttachmentFileName = (attachment) => {
  if (!attachment) return '未知文件'
  // 如果是对象，优先使用原始文件名
  if (typeof attachment === 'object') {
    if (attachment.originalFileName) {
      return attachment.originalFileName
    }
    if (attachment.fileName) {
      return attachment.fileName
    }
    if (attachment.name) {
      return attachment.name
    }
    if (attachment.url) {
      const parts = attachment.url.split('/')
      const fileName = parts[parts.length - 1].split('?')[0]
      return fileName || '附件'
    }
  }
  // 如果是字符串URL
  if (typeof attachment === 'string') {
    const parts = attachment.split('/')
    const fileName = parts[parts.length - 1].split('?')[0]
    return fileName || '附件'
  }
  return '未知文件'
}

// 获取附件URL
const getAttachmentUrl = (attachment) => {
  let url = ''
  if (typeof attachment === 'string') {
    url = attachment
  } else if (attachment && attachment.url) {
    url = attachment.url
  } else {
    return ''
  }
  
  // 如果是blob URL，直接返回（不需要代理）
  if (url.startsWith('blob:')) {
    return url
  }
  
  // 对于COS URL，使用代理接口（解决403和CORS问题）
  return getPreviewUrl(url)
}

// 获取附件大小
const getAttachmentSize = (attachment) => {
  if (!attachment) return 0
  if (typeof attachment === 'object' && attachment.size) {
    return attachment.size
  }
  return 0
}

// 获取所有图片的预览列表（用于 el-image 的预览功能）
const getImagePreviewList = () => {
  if (!detailInfo.value || !detailInfo.value.attachments) return []
  return detailInfo.value.attachments
    .filter(att => isAttachmentImage(att) && getAttachmentUrl(att))
    .map(att => getAttachmentUrl(att))
}

// 获取当前图片在预览列表中的索引
const getImageIndex = (currentIndex) => {
  if (!detailInfo.value || !detailInfo.value.attachments) return 0
  let imageIndex = 0
  for (let i = 0; i < currentIndex; i++) {
    const att = detailInfo.value.attachments[i]
    if (att && isAttachmentImage(att) && getAttachmentUrl(att)) {
      imageIndex++
    }
  }
  return imageIndex
}

// 处理图片加载错误
const handleImageError = (event) => {
  if (event.target) {
    event.target.style.display = 'none'
  }
}

// 获取文件扩展名
const getFileExtension = (attachment) => {
  const fileName = getAttachmentFileName(attachment)
  if (!fileName) return ''
  const parts = fileName.split('.')
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : ''
}

// 获取文件类型图标
const getFileIcon = (attachment) => {
  const ext = getFileExtension(attachment)
  // 图片类型
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
    return Picture
  }
  // 视频类型
  if (['mp4', 'avi', 'mov', 'wmv', 'flv'].includes(ext)) {
    return VideoPlay
  }
  // 其他类型使用文档图标
  return Document
}

// 获取文件类型标签（显示在图标上）
const getFileTypeLabel = (attachment) => {
  const ext = getFileExtension(attachment)
  const labelMap = {
    'xls': 'XLS',
    'xlsx': 'XLSX',
    'doc': 'DOC',
    'docx': 'DOCX',
    'pdf': 'PDF',
    'txt': 'TXT',
    'ppt': 'PPT',
    'pptx': 'PPTX',
    'zip': 'ZIP',
    'rar': 'RAR',
    '7z': '7Z'
  }
  return labelMap[ext] || ''
}

// 获取文件类型图标样式类
const getFileIconClass = (attachment) => {
  const ext = getFileExtension(attachment)
  const iconClassMap = {
    // Excel
    'xls': 'icon-excel',
    'xlsx': 'icon-excel',
    // Word
    'doc': 'icon-word',
    'docx': 'icon-word',
    // PDF
    'pdf': 'icon-pdf',
    // 文本
    'txt': 'icon-txt',
    // 图片
    'jpg': 'icon-image',
    'jpeg': 'icon-image',
    'png': 'icon-image',
    'gif': 'icon-image',
    'bmp': 'icon-image',
    'webp': 'icon-image',
    // PPT
    'ppt': 'icon-ppt',
    'pptx': 'icon-ppt',
    // 压缩包
    'zip': 'icon-zip',
    'rar': 'icon-zip',
    '7z': 'icon-zip'
  }
  return iconClassMap[ext] || 'icon-default'
}

// 下载附件
const handleDownloadAttachment = async (attachment) => {
  let url = typeof attachment === 'string' ? attachment : attachment.url
  let fileName = typeof attachment === 'string' ? undefined : (attachment.originalFileName || attachment.fileName)
  
  if (!url) {
    ElMessage.warning('附件地址无效')
    return
  }
  
  try {
    await downloadFileFromServer(url, fileName)
  } catch (error) {
    console.error('下载附件失败:', error)
    ElMessage.error(error.message || '下载附件失败')
  }
}

// 详情中直接提交审批（不依赖表单 ref，直接调接口）
const handleSubmitFromDetail = async () => {
  if (!detailInfo.value || !detailInfo.value.id) return

  try {
    // 个人用户不需要确认弹窗，直接提交
    if (!isPersonalUser.value) {
      await ElMessageBox.confirm('确定要提交该报销申请进行审批吗？', '提示', {
        type: 'warning'
      })
    }

    submitLoading.value = true
    const payload = {
      id: detailInfo.value.id,
      expenseAmount: detailInfo.value.expenseAmount ? Number(detailInfo.value.expenseAmount) : null,
      expenseReason: detailInfo.value.expenseReason?.trim(),
      expenseType: detailInfo.value.expenseType || 'TRAVEL', // 报销类型，如果为空则默认为差旅报销
      travelDate: detailInfo.value.travelDate,
      endDate: detailInfo.value.endDate || null, // 结束日期
      // 附件：始终传数组，空数组表示清空旧附件
      attachments: Array.isArray(detailInfo.value.attachments)
        ? detailInfo.value.attachments
            .map(att => {
              if (typeof att === 'string') return att
              return att?.url || ''
            })
            .filter(url => url)
        : []
    }

    const res = await submitTravelExpense(payload)
    if (res && (res.code === '000000' || res.success)) {
      ElMessage.success('提交成功')
      detailDialogVisible.value = false
      // 个人用户提交审批后，自动切换到已通过状态
      queryParams.status = isPersonalUser.value ? 'approved' : 'pending'
      queryParams.page = 1
      handleSearch()
    } else {
      throw new Error(res?.msg || res?.message || '提交失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error(error.message || '提交失败')
    }
  } finally {
    submitLoading.value = false
  }
}

const handleSizeChange = (val) => {
  queryParams.pageSize = val
  queryParams.page = 1
  loadExpenseList()
}

const handlePageChange = (val) => {
  queryParams.page = val
  loadExpenseList()
}

onMounted(() => {
  loadExpenseList()
})
</script>

<style scoped>
.travel-expense-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-form {
  margin-bottom: 20px;
  /* 再向左偏移一些，使筛选条件与表格左侧位置更贴近（参考合同管理） */
  margin-left: -30px;
}

.action-buttons-inline {
  display: flex;
  gap: 8px;
}

/* 表格容器，确保自适应 */
.table-wrapper {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
}

/* 表格自适应优化 */
.table-wrapper :deep(.el-table) {
  width: 100%;
  min-width: 100%;
}

/* 小屏幕下优化表格显示 */
@media (max-width: 1400px) {
  .table-wrapper :deep(.el-table) {
    font-size: 13px;
  }
  
  .table-wrapper :deep(.el-table .el-button) {
    padding: 5px 10px;
    font-size: 12px;
  }
}

@media (max-width: 1200px) {
  .table-wrapper :deep(.el-table) {
    font-size: 12px;
  }
  
  .table-wrapper :deep(.el-table .el-button) {
    padding: 4px 8px;
    font-size: 11px;
  }
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.expense-detail-layout {
  display: flex;
  gap: 16px;
}

.expense-detail-left {
  flex: 2;
}

.expense-detail-right {
  flex: 1.5;
}

.approval-record-card {
  height: 100%;
}

.footer-center {
  display: flex;
  justify-content: center;
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.footer-right {
  display: flex;
  justify-content: flex-end;
}

.add-image-btn {
  width: 80px;
  height: 80px;
  border: 1px dashed #d9d9d9;
  background-color: #fafafa;
  color: #8c8c8c;
  transition: all 0.3s;
}

.add-image-btn:hover {
  border-color: #409eff;
  color: #409eff;
  background-color: #f0f9ff;
}

/* 附件列表样式（参考合同详情） */
.detail-attachments {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-attachments .attachment-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  transition: all 0.3s;
}

.detail-attachments .attachment-item:hover {
  background-color: #f0f2f5;
  border-color: #c0c4cc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.attachment-icon-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-right: 12px;
  flex-shrink: 0;
  border-radius: 6px;
  background-color: #f5f7fa;
  position: relative;
}

.attachment-icon {
  font-size: 24px;
}

.file-type-label {
  position: absolute;
  bottom: 2px;
  left: 0;
  right: 0;
  font-size: 9px;
  font-weight: 600;
  text-align: center;
  line-height: 1;
  color: #fff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.attachment-name {
  flex: 1;
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 12px;
}

.attachment-size {
  font-size: 12px;
  color: #909399;
  margin-right: 12px;
  flex-shrink: 0;
  min-width: 50px;
}

.attachment-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* 附件列表样式 */
.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.attachment-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 10px;
  background-color: #f8f9fa;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  transition: all 0.3s;
}

.attachment-item:hover {
  background-color: #f0f2f5;
  border-color: #c0c4cc;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 图片附件样式 */
.attachment-item-image {
  display: flex;
  align-items: flex-start;
  padding: 14px;
  border: 1px solid #e4e7ed;
  background-color: #ffffff;
}

.attachment-item-image:hover {
  border-color: #c0c4cc;
}

.attachment-image-wrapper {
  position: relative;
  width: 100px;
  height: 100px;
  margin-right: 14px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background-color: #f5f7fa;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.attachment-image-wrapper:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  transform: scale(1.02);
}

.attachment-image-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.attachment-image-preview:hover {
  transform: scale(1.05);
}

.image-slot {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background: #f5f7fa;
  color: #909399;
  font-size: 20px;
}

.download-overlay {
  position: absolute;
  bottom: 4px;
  right: 4px;
  opacity: 1;
  z-index: 3;
}

.download-overlay-btn {
  background-color: rgba(64, 158, 255, 0.9);
  border: none;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.download-overlay-btn:hover {
  background-color: #409eff;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.4);
}

.attachment-image-info {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 14px;
}

.attachment-image-details {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.attachment-image-details .attachment-name {
  font-size: 14px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 0;
}

.attachment-image-details .attachment-size {
  font-size: 12px;
  color: #909399;
  margin-right: 0;
}

/* Excel 图标样式 */
.icon-excel {
  background: linear-gradient(135deg, #1d6f42 0%, #2a8f56 100%);
}

.icon-excel .attachment-icon {
  color: #fff;
}

/* Word 图标样式 */
.icon-word {
  background: linear-gradient(135deg, #2b579a 0%, #3d6fb8 100%);
}

.icon-word .attachment-icon {
  color: #fff;
}

/* PDF 图标样式 */
.icon-pdf {
  background: linear-gradient(135deg, #d04423 0%, #e85a3a 100%);
}

.icon-pdf .attachment-icon {
  color: #fff;
}

/* 文本图标样式 */
.icon-txt {
  background: linear-gradient(135deg, #666666 0%, #808080 100%);
}

.icon-txt .attachment-icon {
  color: #fff;
}

/* 图片图标样式 */
.icon-image {
  background: linear-gradient(135deg, #4a90e2 0%, #5ba3f5 100%);
}

.icon-image .attachment-icon {
  color: #fff;
}

/* PPT 图标样式 */
.icon-ppt {
  background: linear-gradient(135deg, #d04423 0%, #e85a3a 100%);
}

.icon-ppt .attachment-icon {
  color: #fff;
}

/* 压缩包图标样式 */
.icon-zip {
  background: linear-gradient(135deg, #ff9800 0%, #ffb340 100%);
}

.icon-zip .attachment-icon {
  color: #fff;
}

/* 默认图标样式 */
.icon-default {
  background-color: #909399;
}

.icon-default .attachment-icon {
  color: #fff;
}

/* 提交审批按钮淡蓝色样式 */
.submit-approval-btn {
  background-color: #5B9BD5;
  border-color: #5B9BD5;
  color: #fff;
}

.submit-approval-btn:hover {
  background-color: #4A8BC2;
  border-color: #4A8BC2;
}

.submit-approval-btn:disabled {
  background-color: #C0C4CC;
  border-color: #C0C4CC;
  color: #fff;
}
</style>

